// Ao concluir: "Feito!" com meme animado, confetes e o resultado comparado com a
// estimativa — sem indicar a próxima tarefa (AC-031). Na hora de relaxar do Pomodoro, o
// meme sai para a esquerda e dá lugar ao "Vamos relaxar" (AC-189). Última tarefa do dia:
// pop-up com meme e "Continuar com as tarefas de amanhã / próximas tarefas" (AC-030); com o
// Pomodoro ligado, só "Continuar" o mantém ligado (AC-070). Com "Comemoração" desligada, nada de
// meme nem confetes: só um "Feito!" verde no lugar do título por 0,5 s (AC-230).
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { buildBoard, sectionOf } from '../domain/ordering';
import type { FinishResult } from '../domain/timer';
import type { Celebration as CelebrationData } from '../state/AppStore';
import { useAppState, useStore } from '../state/AppStore';
import { CELEBRATE_MS, DONE_FLASH_MS } from '../state/floatController';
import { useNow } from '../state/useNow';
import { usePomodoro } from '../state/usePomodoro';
import { RelaxScreen } from './BreakScreen';
import { Confetti } from './Confetti';
import { Dialog } from './EditTaskDialog';
import { Icon } from './Icon';
import { memeUrl, pickMeme } from './memes';
import { cls } from './NewTaskForm';

export function resultText(result: FinishResult, t: (k: string, o?: Record<string, unknown>) => string): string {
  return t(`celebrate.result.${result.kind}`, { count: result.minutes });
}

export function MemeImage({ id, className }: { id: string; className?: string }) {
  const { t } = useTranslation();
  return <img className={className ?? 'meme'} src={memeUrl(id)} alt={t('celebrate.memeAlt')} data-meme={id} draggable={false} />;
}

export function Celebration({ data }: { data: CelebrationData }) {
  const { t } = useTranslation();
  const store = useStore();
  // um meme novo a cada tarefa concluída
  const meme = useMemo(() => pickMeme(), [data.taskId]);
  // some sozinho no mesmo tempo da comemoração da barra flutuante (AC-031)
  useEffect(() => {
    const id = setTimeout(() => store.dismissCelebration(), CELEBRATE_MS);
    return () => clearTimeout(id);
  }, [data.taskId, store]);

  return (
    <section className="celebrate" data-testid="celebration" role="status">
      <Confetti />
      <button type="button" className="icon-btn celebrate__close" aria-label={t('common.close')} onClick={() => store.dismissCelebration()}>
        <Icon name="x" size={14} />
      </button>
      <h2 className="celebrate__title">{t('celebrate.title')}</h2>
      <MemeImage id={meme} />
      <p className="celebrate__body">{resultText(data.result, t)}</p>
    </section>
  );
}

/** "Comemoração" desligada (AC-230): o título da tarefa em andamento dá lugar a "Feito!" verde por 0,5 s. */
export function DoneFlash({ data }: { data: CelebrationData }) {
  const { t } = useTranslation();
  const store = useStore();
  useEffect(() => {
    const id = setTimeout(() => store.dismissCelebration(), DONE_FLASH_MS);
    return () => clearTimeout(id);
  }, [data.taskId, store]);
  return (
    <section className="current current--done" data-testid="done-flash">
      <div className="current__top">
        <h2 className="current__title current__done-flash" role="status">
          {t('float.done')}
        </h2>
      </div>
    </section>
  );
}

/** Com a comemoração ligada (padrão) mostra meme e confetes (AC-230). */
export function useCelebrationOn(): boolean {
  return useAppState((s) => s.profile?.celebration_enabled !== false);
}

/** "Feito!" e, na hora de relaxar, o meme sai para a esquerda e entra o "Vamos relaxar" (AC-189). */
export function CelebrationStage({ celebration, ready, canEdit }: { celebration: CelebrationData | null; ready: boolean; canEdit: boolean }) {
  // a comemoração continua desenhada enquanto sai para a esquerda
  const last = useRef(celebration);
  if (celebration) last.current = celebration;
  else if (!ready) last.current = null;
  const shown = celebration ?? last.current;
  return (
    <div className="stage">
      <div className={cls('stage__track', ready && !celebration && 'stage__track--relax')}>
        <div className="stage__pane" aria-hidden={!celebration}>
          {shown && <Celebration data={shown} />}
        </div>
        {ready && (
          <div className="stage__pane">
            <RelaxScreen canEdit={canEdit} />
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Fim do dia (AC-030): pop-up com meme e confetes. Com mais tarefas na lista,
 * "Continuar com as tarefas de amanhã" (ou "com as próximas tarefas") inicia a próxima.
 * Com o Pomodoro ligado, "Continuar" o mantém ligado; descansar ou fechar o desliga (AC-070).
 */
export function DayDoneDialog({ canEdit }: { canEdit: boolean }) {
  const { t } = useTranslation();
  const store = useStore();
  const { controller: pomodoro } = usePomodoro();
  const pomodoroOn = useAppState((s) => !!s.profile?.pomodoro_enabled);
  const tasks = useAppState((s) => s.tasks);
  const now = useNow();
  const meme = useMemo(() => pickMeme(), []);
  const loud = useCelebrationOn();
  const today = new Date(now);
  const board = buildBoard(tasks, today);
  const next = board.current ? null : board.next;
  const close = () => store.dismissCelebration();
  const rest = () => {
    close();
    if (pomodoroOn) void pomodoro.setEnabled(false);
  };
  return (
    <Dialog labelId="day-done-title" onClose={rest}>
      <div className="day-done" data-testid="end-of-day">
        {loud && <Confetti />}
        <h2 id="day-done-title" className="dialog__title">
          {t('celebrate.dayTitle')}
        </h2>
        {loud && <MemeImage id={meme} />}
        <p className="dialog__body">{t('celebrate.dayBody')}</p>
        <div className="day-done__actions">
          {next && (
            <button
              type="button"
              className="btn btn--primary"
              disabled={!canEdit}
              onClick={() => {
                close();
                void store.start(next.id);
              }}
            >
              <Icon name="play" size={12} filled />
              {sectionOf(next, today) === 'tomorrow' ? t('celebrate.dayContinueTomorrow') : t('celebrate.dayContinueNext')}
            </button>
          )}
          <button type="button" className="btn btn--add" onClick={rest}>
            {t('celebrate.rest')}
          </button>
        </div>
      </div>
    </Dialog>
  );
}
